import type { DashboardSellerForOrder } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useCreateOrder } from "@/services";
import { useDebounce } from "@/utils";
import { formatNumber } from "@/utils/tools";
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	Input,
	Typography,
} from "@st-marketplace/ui";
import { useId, useState } from "react";
import AddOrderDataTable from "./DataTable";
import AddOrderForm, { type AddOrderAccountData } from "./Form";

interface AddOrderDialogProps {
	open: boolean;
	onClose: () => void;
}

const OrdersAddDialog = ({ open, onClose }: AddOrderDialogProps) => {
	const { t } = useTranslation();
	const createOrderMutation = useCreateOrder();
	const amountErrorId = useId();
	const [amount, setAmount] = useState("");
	const [selectedSeller, setSelectedSeller] =
		useState<DashboardSellerForOrder | null>(null);
	const [formResetKey, setFormResetKey] = useState(0);
	const [accountData, setAccountData] = useState<AddOrderAccountData>({
		email: "",
		password: "",
		authType: "backup_code",
		gauth: "",
		backup_code: [],
	});
	const parsedAmount = Number(amount);
	const debouncedAmount = useDebounce(amount, 500);
	const validAmount =
		Number.isSafeInteger(parsedAmount * 1000) &&
		parsedAmount > 0 &&
		Number.isInteger(parsedAmount);

	const reset = () => {
		setAmount("");
		setSelectedSeller(null);
		setFormResetKey((key) => key + 1);
		setAccountData({
			email: "",
			password: "",
			authType: "backup_code",
			gauth: "",
			backup_code: [],
		});
	};

	const close = () => {
		reset();
		onClose();
	};

	const submit = (data: AddOrderAccountData) => {
		if (
			!selectedSeller ||
			!validAmount ||
			amount !== debouncedAmount ||
			createOrderMutation.isPending
		)
			return;
		createOrderMutation.mutate(
			{
				body: {
					amount: parsedAmount * 1000,
					platform: "ps",
					ea_email: data.email,
					ea_password: data.password,
					auth:
						data.authType === "gauth"
							? { type: "gauth", auth: data.gauth.trim() }
							: { type: "backup_code", auth: data.backup_code },
					relation_id: selectedSeller.relation_id,
				},
			},
			{ onSuccess: reset },
		);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(nextOpen) =>
				!nextOpen && !createOrderMutation.isPending && close()
			}
		>
			<DialogContent
				className="flex min-h-0 flex-col gap-0 overflow-hidden p-0"
				style={{
					width: "min(1280px, calc(100vw - 32px))",
					maxWidth: "1280px",
					height: "min(800px, calc(100vh - 32px))",
					maxHeight: "calc(100vh - 32px)",
				}}
			>
				<DialogHeader className="shrink-0">
					<DialogTitle>{t("addOrder.title")}</DialogTitle>
				</DialogHeader>
				<div className="min-h-0 flex-1 overflow-y-auto lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(360px,0.6fr)] lg:overflow-hidden">
					<section
						className="min-w-0 space-y-5 p-5 lg:min-h-0 lg:overflow-y-auto lg:border-e lg:border-secondary-border lg:p-6"
						aria-label={t("addOrder.chooseSeller")}
					>
						<div>
							<Typography variant="title-sm" as="h2">
								{t("addOrder.chooseSeller")}
							</Typography>
							<Typography
								variant="body-md"
								className="mt-1 text-secondary-text"
							>
								{t("addOrder.amountHelp")}
							</Typography>
						</div>
						<div className="max-w-sm">
							<label
								htmlFor="add-order-amount"
								className="mb-2 block font-medium"
							>
								{t("addOrder.inputAmount")} (
								{t("signs.thousands").toUpperCase()})
							</label>
							<Input
								id="add-order-amount"
								type="number"
								autoFocus
								min="1"
								step="1"
								inputMode="numeric"
								suffix={
									<span aria-hidden="true">
										{t("signs.thousands").toUpperCase()}
									</span>
								}
								value={amount}
								disabled={createOrderMutation.isPending}
								aria-describedby={
									amount !== "" && !validAmount ? amountErrorId : undefined
								}
								aria-invalid={amount !== "" && !validAmount}
								onChange={(event) => {
									setAmount(event.target.value);
									setSelectedSeller(null);
								}}
							/>
							{amount !== "" && !validAmount && (
								<p
									id={amountErrorId}
									role="alert"
									className="mt-1 text-sm text-danger-text"
								>
									{t("addOrder.amountError")}
								</p>
							)}
						</div>
						{validAmount && (
							<AddOrderDataTable
								key={amount}
								amount={parsedAmount}
								pending={amount !== debouncedAmount}
								selectedRelationId={selectedSeller?.relation_id}
								onSelect={setSelectedSeller}
							/>
						)}
					</section>
					<section
						className="min-w-0 border-t border-secondary-border p-5 lg:min-h-0 lg:overflow-y-auto lg:border-t-0 lg:p-6"
						aria-label={t("addOrder.accountTitle")}
					>
						<AddOrderForm
							key={formResetKey}
							formId="add-order-account-form"
							loading={createOrderMutation.isPending}
							initialValues={accountData}
							onSubmit={submit}
						/>
					</section>
				</div>
				<div className="flex shrink-0 flex-col items-stretch gap-3 border-t border-secondary-border px-6 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-5">
					{selectedSeller && validAmount && (
						<div
							id="add-order-submit-help"
							className="min-w-0 text-sm text-secondary-text sm:text-end"
							aria-live="polite"
						>
							<p>
								<RelationNameLink
									name={selectedSeller.seller_name}
									relationId={selectedSeller.relation_id}
									party="seller"
								/>{" "}
								· {formatNumber(parsedAmount * 1000, "count")}
							</p>
							<p className="font-semibold">
								{t("addOrder.estimatedTotal")}:{" "}
								{formatNumber(
									Number(
										(
											(Number(selectedSeller.unit_price) * parsedAmount) /
											1000
										).toFixed(2),
									),
									"price",
								)}
							</p>
						</div>
					)}
					<Button
						type="submit"
						form="add-order-account-form"
						color="primary"
						className="w-full shrink-0 sm:w-auto"
						loading={createOrderMutation.isPending}
						disabled={
							!validAmount || !selectedSeller || createOrderMutation.isPending
						}
						aria-describedby={
							selectedSeller && validAmount
								? "add-order-submit-help"
								: undefined
						}
					>
						{t("addOrder.placeOrder")}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default OrdersAddDialog;
